import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { Save } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import ExpiryBadge from '@/components/ExpiryBadge';
import { ErrorState, LoadingState } from '@/components/States';
import { useAppToast } from '@/lib/toast';
import { dateInput, errorMessage, firstRow } from '@/lib/format';
import { expiryPhrase } from '@/lib/expiry';
import ImageField from './ImageField';
import Panel from '@/components/Panel';
import Textarea from '@/components/Textarea';

const BLANK = {
  name: '',
  sku: '',
  brand: '',
  category: '',
  batch_no: '',
  quantity: '',
  unit: 'pcs',
  unit_price: '',
  manufactured_date: '',
  expiry_date: '',
  status: 'active',
  notes: '',
};

/**
 * Add or edit one batch.
 *
 * The photo is handled in two steps on create — the row is saved first, then
 * the image is posted against its new id — because a product that has no id yet
 * has nowhere to hang an object key. A photo that fails to upload therefore
 * never costs the user the rest of the form: the product is already saved, and
 * the failure is reported as what it is.
 */
export default function ProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const toast = useAppToast();
  const { fetchApi } = useAppApi();

  const [form, setForm] = useState(BLANK);
  const [image, setImage] = useState(null); // the CURRENT photo: a URL, a fresh data URL, or null
  const [imageDirty, setImageDirty] = useState(false);
  const [vocab, setVocab] = useState({ categories: [], units: [], statuses: [] });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key) => (eventOrValue) => {
    const value = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  };

  const load = useCallback(async () => {
    try {
      // The vocabularies are served by the API so a category added on the
      // server appears here without a client rebuild.
      const vocabRes = await axios.get('/api/meta/vocab', { withCredentials: true });
      setVocab(vocabRes.data || {});
      if (!isEdit) return;

      setLoading(true);
      const res = await fetchApi('productDetail', id);
      const row = (res && res.data) || firstRow(res);
      if (!row) throw new Error('That product no longer exists');
      setForm({
        ...BLANK,
        ...row,
        brand: row.brand || '',
        category: row.category || '',
        batch_no: row.batch_no || '',
        notes: row.notes || '',
        quantity: row.quantity ?? '',
        unit_price: row.unit_price ?? '',
        manufactured_date: dateInput(row.manufactured_date),
        expiry_date: dateInput(row.expiry_date),
      });
      setImage(row.image_url || null);
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not load this product'));
    } finally {
      setLoading(false);
    }
    // fetchApi identity changes each render in the library hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => {
    load();
  }, [load]);

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'A product name is required';
    if (!form.expiry_date) errors.expiry_date = 'An expiry date is what this app tracks — please set one';
    if (form.manufactured_date && form.expiry_date && form.manufactured_date > form.expiry_date) {
      errors.expiry_date = 'The expiry date cannot be earlier than the manufactured date';
    }
    if (form.quantity !== '' && Number(form.quantity) < 0) errors.quantity = 'Quantity cannot be negative';
    if (form.unit_price !== '' && Number(form.unit_price) < 0) errors.unit_price = 'Price cannot be negative';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** Persist the photo against a saved row. Returns a message if it failed. */
  const saveImage = async (productId) => {
    if (!imageDirty) return null;
    try {
      if (!image) await fetchApi('productClearImage', productId);
      else if (image.startsWith('data:')) await fetchApi('productUploadImage', { product_id: productId, image });
      return null;
    } catch (ex) {
      return errorMessage(ex, 'The photo could not be saved');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || undefined, // blank → the server generates ZRZ-0001
        brand: form.brand.trim() || null,
        category: form.category || null,
        batch_no: form.batch_no.trim() || null,
        quantity: form.quantity === '' ? 0 : Number(form.quantity),
        unit: form.unit,
        unit_price: form.unit_price === '' ? 0 : Number(form.unit_price),
        manufactured_date: form.manufactured_date || null,
        expiry_date: form.expiry_date,
        status: form.status,
        notes: form.notes.trim() || null,
      };

      let productId = id;
      if (isEdit) {
        await fetchApi('crud', { model: 'Product', action: 'update', where: { id }, data: payload });
      } else {
        const res = await fetchApi('crud', { model: 'Product', action: 'create', data: payload });
        const created = firstRow(res);
        productId = created && created.id;
      }

      const imageError = productId ? await saveImage(productId) : 'The product was saved without its photo';
      if (imageError) toast.warning('Saved without the photo', imageError);
      else toast.success(isEdit ? 'Product updated' : 'Product added', form.name.trim());

      nav(productId ? `/products/${productId}` : '/products');
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not save this product'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState rows={8} />;
  if (err && isEdit && !form.name) return <ErrorState message={err} onRetry={load} />;

  // Show what the chosen date means while the form is still open.
  const previewDays = form.expiry_date
    ? Math.round(
        (new Date(`${form.expiry_date}T00:00:00`).getTime() - new Date(new Date().toDateString()).getTime()) / 86400000
      )
    : null;

  return (
    <div className="space-y-5">
      <PageHeader
        title={isEdit ? 'Edit product' : 'Add product'}
        subtitle={isEdit ? form.name : 'A new batch in the warehouse.'}
        trail={[{ label: 'Products', to: '/products' }, { label: isEdit ? 'Edit' : 'New' }]}
      />

      {err ? (
        <Alert variant="error" title="Could not save" dismissible onDismiss={() => setErr('')}>
          {err}
        </Alert>
      ) : null}

      <form onSubmit={submit} className="space-y-4" noValidate>
        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Identity</h2>
          <ImageField
            value={image}
            name={form.name}
            disabled={saving}
            onChange={(next) => {
              setImage(next);
              setImageDirty(true);
            }}
            onError={(message) => setErr(message)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Product name" value={form.name} onChange={set('name')} error={fieldErrors.name} required placeholder="Full Cream Milk 1L" />
            <Input label="SKU" value={form.sku} onChange={set('sku')} placeholder="Leave blank to generate" />
            <Input label="Brand" value={form.brand} onChange={set('brand')} placeholder="Amul" />
            <Select
              label="Category"
              options={[{ value: '', label: 'Uncategorised' }, ...(vocab.categories || []).map((c) => ({ value: c, label: c }))]}
              value={form.category}
              onChange={set('category')}
              placeholder="Uncategorised"
            />
          </div>
        </Panel>

        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Batch &amp; dates</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input label="Batch number" value={form.batch_no} onChange={set('batch_no')} placeholder="B02400" />
            <Input label="Manufactured on" type="date" value={form.manufactured_date} onChange={set('manufactured_date')} />
            <Input
              label="Expires on"
              type="date"
              value={form.expiry_date}
              onChange={set('expiry_date')}
              error={fieldErrors.expiry_date}
              required
            />
            <Select
              label="Status"
              options={(vocab.statuses || ['active']).map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
              value={form.status}
              onChange={set('status')}
            />
          </div>
          {previewDays != null ? (
            <div className="text-sm text-[var(--app-muted)]">
              {expiryPhrase(previewDays)}
              {isEdit && form.expiry_state ? (
                <span className="ml-2 inline-block align-middle">
                  <ExpiryBadge state={form.expiry_state} days={form.days_to_expiry} size="xs" />
                </span>
              ) : null}
            </div>
          ) : null}
        </Panel>

        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Stock</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Input label="Quantity" type="number" min="0" step="0.01" value={form.quantity} onChange={set('quantity')} error={fieldErrors.quantity} placeholder="0" />
            <Select
              label="Unit"
              options={(vocab.units || ['pcs']).map((u) => ({ value: u, label: u }))}
              value={form.unit}
              onChange={set('unit')}
            />
            <Input label="Unit price (₹)" type="number" min="0" step="0.01" value={form.unit_price} onChange={set('unit_price')} error={fieldErrors.unit_price} placeholder="0.00" />
          </div>
          <Textarea label="Notes" value={form.notes} onChange={set('notes')} rows={3} placeholder="Anything worth remembering about this batch" />
        </Panel>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => nav(-1)} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} leftIcon={<Save size={16} />}>
            {isEdit ? 'Save changes' : 'Add product'}
          </Button>
        </div>
      </form>
    </div>
  );
}
